"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Calendar, ShieldCheck, Clock, CircleCheck as CheckCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const trustBadges = [{
  text: "24/7 Fast Local Response"
}, {
  text: "Licensed & Insured Technicians"
}, {
  text: "Upfront & Honest Pricing"
}];
export default function ProblemsHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="problemshero" data-nav="light" className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-24 border-b border-border">
      {/* Decorative ambient background accents */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-40">
        <div className="size-[500px] rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="mx-auto max-w-3xl text-center">
          {/* Emergency Alert Tag */}
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/80 px-4 py-1.5 text-xs sm:text-sm font-medium text-secondary-foreground shadow-2xs mb-6">
            <Clock className="size-4 text-primary shrink-0" />
            <span>24/7 Emergency Diagnosis & Fast Repair</span>
          </motion.div>

          {/* Main H1 Title */}
          <motion.h1 variants={itemVariants} className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-tight sm:leading-tight">
            Plumbing Problems in Jonesboro, GA
          </motion.h1>

          {/* Lead Paragraph */}
          <motion.p variants={itemVariants} className="mt-6 text-base sm:text-lg md:text-xl leading-relaxed text-muted-foreground font-default">
            From sudden pipe bursts and stubborn drain clogs to water heater breakdowns, our licensed local plumbers quickly diagnose your emergency and restore safe, reliable plumbing to your home.
          </motion.p>

          {/* Dual Action CTA Buttons */}
          <motion.div variants={itemVariants} className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-colors">
              <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="size-5 shrink-0" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-semibold border-border bg-card text-card-foreground hover:bg-accent hover:text-accent-foreground shadow-xs transition-colors">
              <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                <SafeHydrate><Calendar className="size-5 shrink-0" /></SafeHydrate>
                <span>Request Service</span>
              </Link>
            </Button>
          </motion.div>

          {/* Trust Highlights */}
          <motion.div variants={itemVariants} className="mt-10 sm:mt-12 pt-8 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-4">
            {trustBadges.map((badge, i) => <div key={badge.text} data-index={i} className="flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-muted-foreground">
                <CheckCircle className="size-4 text-accent shrink-0" />
                <span>{badge.text}</span>
              </div>)}
          </motion.div>
        </motion.div>
      </div>
    </section>;
}